import React from 'react';
import { Box, Card, Typography, Alert, CircularProgress } from '@mui/material';
import { ConfirmDialog } from '@personal-dashboard/ui';

import { QuizStats } from './QuizStats';
import { QuizHeaderControls } from './QuizHeaderControls';
import { QuizContent } from './QuizContent';
import { QuizResultScreen } from './QuizResultScreen';
import { useQuizGame } from '../hooks/useQuizGame';

interface QuizCardProps {
  setId: number;
  onBack: () => void;
}

export const QuizCard: React.FC<QuizCardProps> = ({ setId, onBack }) => {
  const {
    loading,
    error,
    mode,
    setMode,
    isReverse,
    currentWord,
    userInput,
    setUserInput,
    isProcessing,
    feedback,
    stats,
    currentWordMastery,
    isHighlighted,
    isResetConfirmOpen,
    inputRef,
    getFillSentence,
    triggerResetSet,
    handleConfirmReset,
    handleCancelReset,
    handleSubmit,
  } = useQuizGame({ setId });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading quiz progress: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 3 }}>
      <QuizHeaderControls
        onBack={onBack}
        mode={mode}
        onCycleMode={() => {
          setMode((prev) => {
            if (prev === 'normal') return 'reverse';
            if (prev === 'reverse') return 'fill';
            return 'normal';
          });
        }}
        onReset={triggerResetSet}
      />

      <QuizStats setName={stats.name} mastered={stats.mastered} remaining={stats.remaining} />

      <Card
        sx={{
          p: 4,
          borderRadius: 4,
          boxShadow: 4,
          position: 'relative',
          backgroundColor: 'background.paper',
          color: 'text.primary',
        }}
      >
        {!currentWord ? (
          <QuizResultScreen onBack={onBack} highlighted={isHighlighted} />
        ) : (
          <QuizContent
            currentWord={currentWord}
            mode={mode}
            isReverse={isReverse}
            currentWordMastery={currentWordMastery}
            isHighlighted={isHighlighted}
            userInput={userInput}
            isProcessing={isProcessing}
            feedback={feedback}
            onInputChange={setUserInput}
            onSubmit={handleSubmit}
            inputRef={inputRef}
            getFillSentence={getFillSentence}
            onBack={onBack}
          />
        )}
      </Card>

      <ConfirmDialog
        open={isResetConfirmOpen}
        title="Reset Progress?"
        message="Are you sure you want to reset all progress for this set? This action cannot be undone."
        confirmLabel="Reset"
        cancelLabel="Cancel"
        severity="error"
        onConfirm={handleConfirmReset}
        onCancel={handleCancelReset}
      />
    </Box>
  );
};
